import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import './css/BaobabDesignSystem.css';

const NomenclatureTest = () => {
    const { user } = useAuth();
    const [nomenclatureData, setNomenclatureData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

    const fetchNomenclatureTest = async () => {
        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/test-nomenclature`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const data = await response.json();
            setNomenclatureData(data);
        } catch (error) {
            console.error('Erreur lors du test de nomenclature:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const resetCounters = async () => {
        if (!window.confirm('Êtes-vous sûr de vouloir réinitialiser tous les compteurs de nomenclature ?')) {
            return;
        }

        setLoading(true);
        setError(null);
        
        try {
            const response = await fetch(`${API_BASE_URL}/api/reset-counters`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('token')}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error(`Erreur HTTP: ${response.status}`);
            }

            const data = await response.json();
            alert(`Compteurs réinitialisés: ${data.compteurs_supprimes} compteurs supprimés`);
            
            // Recharger les données après reset
            await fetchNomenclatureTest();
        } catch (error) {
            console.error('Erreur lors de la réinitialisation:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchNomenclatureTest();
    }, []);

    const formatCodeColor = (code) => {
        const colors = {
            'OPP': '#3B82F6',  // Bleu pour opportunités
            'OFF': '#10B981',  // Vert pour offres
            'OP': '#F59E0B',   // Orange pour opérations
            'BDC': '#EF4444',  // Rouge pour factures
            'BDCST': '#8B5CF6' // Violet pour bons de commande
        };
        
        const prefix = code.split('-')[0];
        return colors[prefix] || '#6B7280';
    };

    const renderNomenclatureCode = (code, label) => (
        <div className="flex items-center space-x-2 mb-2">
            <div 
                className="px-2 py-1 rounded text-white text-xs font-mono font-bold"
                style={{ backgroundColor: formatCodeColor(code) }}
            >
                {code}
            </div>
            <span className="text-sm text-gray-600">{label}</span>
        </div>
    );

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600">Chargement...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                    <div className="text-red-400"></div>
                    <div className="ml-3">
                        <h3 className="text-sm font-medium text-red-800">Erreur</h3>
                        <p className="mt-1 text-sm text-red-700">{error}</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="baobab-container">
            <div className="baobab-header">
                <h1 className="baobab-title">🔢 Test de Nomenclature Automatique</h1>
                <p className="baobab-subtitle">
                    Système de génération automatique des numéros de documents
                </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Formats de nomenclature */}
                <div className="baobab-card">
                    <h2 className="baobab-card-title">📋 Formats de Nomenclature</h2>
                    {nomenclatureData && (
                        <div className="space-y-4">
                            <div>
                                <h3 className="font-semibold text-gray-700 mb-2">📅 Date actuelle: {nomenclatureData.date}</h3>
                                <p className="text-sm text-gray-600 mb-4">Format date: {nomenclatureData.format_date}</p>
                            </div>
                            
                            <div className="space-y-3">
                                <div className="p-3 bg-blue-50 rounded-lg">
                                    <h4 className="font-medium text-blue-800">Opportunités</h4>
                                    <code className="text-sm bg-blue-100 px-2 py-1 rounded">
                                        {nomenclatureData.nomenclature_format.opportunites}
                                    </code>
                                </div>
                                
                                <div className="p-3 bg-green-50 rounded-lg">
                                    <h4 className="font-medium text-green-800">Offres/Devis</h4>
                                    <code className="text-sm bg-green-100 px-2 py-1 rounded">
                                        {nomenclatureData.nomenclature_format.offres}
                                    </code>
                                </div>
                                
                                <div className="p-3 bg-yellow-50 rounded-lg">
                                    <h4 className="font-medium text-yellow-800">Opérations</h4>
                                    <code className="text-sm bg-yellow-100 px-2 py-1 rounded">
                                        {nomenclatureData.nomenclature_format.operations}
                                    </code>
                                </div>
                                
                                <div className="p-3 bg-red-50 rounded-lg">
                                    <h4 className="font-medium text-red-800">Factures</h4>
                                    <code className="text-sm bg-red-100 px-2 py-1 rounded">
                                        {nomenclatureData.nomenclature_format.factures}
                                    </code>
                                </div>
                                
                                <div className="p-3 bg-purple-50 rounded-lg">
                                    <h4 className="font-medium text-purple-800">Bons de Commande</h4>
                                    <code className="text-sm bg-purple-100 px-2 py-1 rounded">
                                        {nomenclatureData.nomenclature_format.bons_commande}
                                    </code>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Légende */}
                <div className="baobab-card">
                    <h2 className="baobab-card-title">🔤 Légende des Segments</h2>
                    {nomenclatureData && (
                        <div className="space-y-2">
                            {Object.entries(nomenclatureData.legende).map(([key, value]) => (
                                <div key={key} className="flex items-center space-x-3">
                                    <span className="font-mono bg-gray-100 px-2 py-1 rounded text-sm font-bold">
                                        {key}
                                    </span>
                                    <span className="text-sm text-gray-600">{value}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* Compteurs actuels */}
            {nomenclatureData && (
                <div className="baobab-card mt-6">
                    <h2 className="baobab-card-title">📊 État des Compteurs</h2>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-4">
                        {nomenclatureData.compteurs.map((counter) => (
                            <div key={counter.type} className="text-center p-4 bg-gray-50 rounded-lg">
                                <div className="text-2xl font-bold text-gray-800">{counter.current_value}</div>
                                <div className="text-sm font-medium text-gray-600">{counter.type}</div>
                                <div className="text-xs text-gray-500">{counter.date}</div>
                            </div>
                        ))}
                    </div>
                    
                    <div className="flex space-x-3">
                        <button
                            onClick={fetchNomenclatureTest}
                            disabled={loading}
                            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50"
                        >
                            🔄 Actualiser
                        </button>
                        
                        {user.role === 'admin' && (
                            <button
                                onClick={resetCounters}
                                disabled={loading}
                                className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50"
                            >
                                🗑️ Réinitialiser Compteurs
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Exemples générés */}
            {nomenclatureData && (
                <div className="baobab-card mt-6">
                    <h2 className="baobab-card-title">🎯 Exemples de Nomenclature Générée</h2>
                    <div className="space-y-6">
                        {nomenclatureData.exemples.map((exemple, index) => (
                            <div key={index} className="border rounded-lg p-4 bg-gray-50">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <h3 className="font-semibold text-gray-800">{exemple.client}</h3>
                                        <p className="text-sm text-gray-600">
                                            Quadrigramme: <span className="font-mono bg-blue-100 px-2 py-1 rounded">
                                                {exemple.client_quadrigramme}
                                            </span>
                                        </p>
                                    </div>
                                    <div>
                                        <h3 className="font-semibold text-gray-800">{exemple.driver}</h3>
                                        <p className="text-sm text-gray-600">
                                            Quadrigramme: <span className="font-mono bg-green-100 px-2 py-1 rounded">
                                                {exemple.driver_quadrigramme}
                                            </span>
                                        </p>
                                    </div>
                                </div>
                                
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div>
                                        <h4 className="font-medium text-gray-700 mb-2">Documents générés:</h4>
                                        {renderNomenclatureCode(exemple.nomenclature.opportunite, "Opportunité")}
                                        {renderNomenclatureCode(exemple.nomenclature.offre, "Offre")}
                                        {renderNomenclatureCode(exemple.nomenclature.operation, "Opération")}
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700 mb-2">Facturation:</h4>
                                        {renderNomenclatureCode(exemple.nomenclature.facture, "Facture")}
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700 mb-2">Transport:</h4>
                                        {renderNomenclatureCode(exemple.nomenclature.bon_commande_transporteur, "Bon de Commande")}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default NomenclatureTest;